// Status tag colors. Mirrors the orders table (`getStatusStyles` in
// list-quotations.component.ts) so the order-detail header and the side-by-side
// panel render the exact same tag as the orders list. Keep in sync with the table.
const STATUS_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  all:                { bg: '#949494', text: '#FFFFFF', border: '' },
  sent_to_erp:        { bg: '#ADE9D1', text: '#006F43', border: '#77DBB3' },
  done:               { bg: '#ADE9D1', text: '#006F43', border: '#77DBB3' },
  sale:               { bg: '#ADE9D1', text: '#006F43', border: '#77DBB3' },
  pending_inventory:  { bg: '#FFF2D0', text: '#765700', border: '#FFEAB0' },
  cancel:             { bg: '#FDD7D0', text: '#821500', border: '#FBBCB0' },
  declined:           { bg: '#FDD7D0', text: '#821500', border: '#FBBCB0' },
  draft:              { bg: '#E2E2E2', text: '#1C2B36', border: '' },
  archived:           { bg: '#F0F0F0', text: '#6B6B6B', border: '#D9D9D9' },
  processing:         { bg: 'rgba(100,73,219,0.10)', text: '#6449DB', border: 'rgba(100,73,219,0.28)' },
  needs_review:       { bg: '#FDD7D0', text: '#821500', border: '#FBBCB0' },
};

const DEFAULT_STYLE = { bg: '#D5F2FF', text: '#004768', border: '#74D3FF' };

export function getQuotationStatusStyles(status: string): { [key: string]: string } {
  const s = STATUS_STYLES[status] ?? DEFAULT_STYLE;
  return {
    'background-color': s.bg,
    'color': s.text,
    'border': `1px solid ${s.border}`,
  };
}

/**
 * A status's ink colour, for surfaces that need the hue without the whole tag —
 * the ring in the orders quick-filter dropdown (WB-8073).
 *
 * Reads the tag's own text colour and nothing else, deliberately: no per-status
 * adjustments live here. The point of the ring is that a status is the same
 * colour in the menu as the badge it filters for, and any "nicer" value invented
 * here would break exactly that. The tag's border colours are the other
 * candidate and were rejected — several are pale enough (#FFEAB0, #FBBCB0) to
 * vanish against white at ring size.
 */
export function getQuotationStatusAccent(status: string): string {
  return (STATUS_STYLES[status] ?? DEFAULT_STYLE).text;
}

// WB-7957/WB-8276: async ERP sync status tag colors, keyed by Connect's own
// sync_status vocabulary (order.erp_sync.sync_status). Separate code space
// from STATUS_STYLES above ('failed'/'canceled' here don't collide with the
// order's own 'cancel'/'declined') -- reuses the same palette so the two
// badges read as one visual language.
const ERP_SYNC_STATUS_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  none:           { bg: '#E2E2E2', text: '#1C2B36', border: '' },
  pending:        { bg: '#D5F2FF', text: '#004768', border: '#74D3FF' },
  processing:     { bg: '#D5F2FF', text: '#004768', border: '#74D3FF' },
  success:        { bg: '#ADE9D1', text: '#006F43', border: '#77DBB3' },
  failed:         { bg: '#FDD7D0', text: '#821500', border: '#FBBCB0' },
  canceled:       { bg: '#E2E2E2', text: '#1C2B36', border: '' },
  not_applicable: { bg: '#E2E2E2', text: '#1C2B36', border: '' },
};

export function getErpSyncStatusStyles(status: string): { [key: string]: string } {
  const s = ERP_SYNC_STATUS_STYLES[status] ?? DEFAULT_STYLE;
  return {
    'background-color': s.bg,
    'color': s.text,
    'border': `1px solid ${s.border}`,
  };
}
